import ReportHdrIcon from './ReportHdrIcon';
import { computeLayoutExpiryDDay, computeExpiryDdayFromIngredient, isIngredientExpiryAlert, type LayoutExpiryColor } from '../themes/expiryStyles';
import { layoutExpiryColorToTone } from '../themes/labels';
import { ExpiryChip } from './labels';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  ButtonBase,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Restaurant as MenusIcon,
  Settings as SettingsIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Analytics as AnalyticsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { FEATURE_MULTI_STORE } from '../config/features';
import { inventoryApi, storeApi } from '../services/api';
import { BadgeProvider } from '../contexts/BadgeContext';
import { 
  Ingredient as IngredientType,
  MenuItem as MenuItemType,
  Order,
  OrderItem,
  User,
  Supplier
} from '../types';
import MobileBottomNavigation from './MobileBottomNavigation';
import {
  desktopMainPaddingBottom,
  desktopMainPaddingTop,
  mobileBottomOffset,
  mobileDrawerHeight,
  mobileHeaderOffset
} from './pageLayout/constants';
import MobileHeader from './MobileHeader';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useScrollHandoff } from '../hooks/useScrollHandoff';
import sessionService from '../services/sessionService';
import { updateAppBadge, initializeAppBadge, debugAppBadge } from '../utils/appBadge';
// iOS 키보드 관련 로직 제거: useKeyboardDetection 미사용


const drawerWidth = 240;
const collapsedDrawerWidth = 72;

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const baseMenuItems: MenuItem[] = [
  { text: '대시보드', icon: <DashboardIcon />, path: '/dashboard' },
  { text: '주문관리', icon: <OrdersIcon />, path: '/orders' },
  { text: '메뉴관리', icon: <MenusIcon />, path: '/menus' },
  { text: '재고관리', icon: <InventoryIcon />, path: '/inventory' },
  { text: '통계분석', icon: <AnalyticsIcon />, path: '/analytics' },
];

interface DDay {
  text: string;
  color: LayoutExpiryColor;
}

// Ingredient 인터페이스에 소비기한 필드 추가
interface ExpiringIngredient extends IngredientType {
  expiryDate: string;
}

/** 데스크톱 헤더 계정 버튼 — 매장/사장님 표기와 아바타를 한 덩어리로 묶은 클릭 영역 */
const accountButtonSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  pl: 1.75,
  pr: 1,
  py: 0.75,
  borderRadius: '10px',
  color: 'inherit',
  maxWidth: { md: 280, lg: 320 },
  transition: 'background-color 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  '&.Mui-focusVisible': {
    outline: '2px solid rgba(255, 255, 255, 0.7)',
    outlineOffset: 2
  }
} as const;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  
  // 전역 navigate 함수 등록 (알림 클릭 시 사용)
  useEffect(() => {
    console.log('🔗 [Layout] navigate 함수 전역 등록 시작');
    
    // App.tsx에서 이미 등록된 navigate 함수가 있는지 확인
    if (!(window as any).__navigate) {
      (window as any).__navigate = navigate;
      console.log('✅ [Layout] navigate 함수 전역 등록 완료');
    } else {
      console.log('⚠️ [Layout] navigate 함수가 이미 등록됨 - App.tsx에서 등록된 함수 사용');
    }
    
    return () => {
      console.log('🗑️ [Layout] navigate 함수 전역 제거');
      // App.tsx에서 등록된 함수가 아닌 경우에만 제거
      // 하지만 알림 클릭 시 navigate 함수가 필요하므로 제거하지 않음
      if ((window as any).__navigate === navigate) {
        console.log('⚠️ [Layout] navigate 함수 제거 방지 - 알림 클릭 시 필요');
        // delete (window as any).__navigate; // 제거하지 않음
      }
    };
  }, [navigate]);
  
  // 세션 만료 감지 및 알림
  useEffect(() => {
    const checkSessionExpiry = () => {
      try {
        // 로그인 상태 확인
        const token = localStorage.getItem('token');
        const currentUser = localStorage.getItem('currentUser');
        
        if (!token || !currentUser) {
          console.log('⚠️ [세션 감지] 로그인되지 않은 상태 - 세션 확인 건너뛰기');
          return;
        }
        
        if (sessionService.isSessionExpiringSoon()) {
          const daysLeft = sessionService.getDaysUntilExpiry();
          console.log(`⚠️ [세션 감지] 세션이 곧 만료됩니다. 남은 기간: ${daysLeft}일`);
          
          // 세션 갱신 페이지로 이동 안내
          if (daysLeft <= 1) {
            console.log('🔔 [세션 감지] 세션 갱신 페이지로 이동 안내');
            // 사용자에게 알림 표시 (선택사항)
            // navigate('/session-renewal');
          }
        } else {
          console.log('✅ [세션 감지] 세션 상태 정상');
        }
      } catch (error) {
        console.error('❌ [세션 감지] 세션 만료 확인 실패:', error);
      }
    };

    // 초기 확인 (로그인 후에만)
    const token = localStorage.getItem('token');
    const currentUser = localStorage.getItem('currentUser');
    if (token && currentUser) {
      checkSessionExpiry();
    }
    
    // 1시간마다 확인
    const interval = setInterval(checkSessionExpiry, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // 모바일: 알림 다이얼로그 테이블 핸드오프
  const lowStockTableRef = useRef<HTMLDivElement | null>(null);
  const expiryTableRef = useRef<HTMLDivElement | null>(null);
  useScrollHandoff(lowStockTableRef);
  useScrollHandoff(expiryTableRef);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    // localStorage에서 collapsed 상태 복원
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [lowStockIngredients, setLowStockIngredients] = useState<IngredientType[]>([]);
  const [showLowStockDialog, setShowLowStockDialog] = useState(false);
  const [lowStockChecked, setLowStockChecked] = useState(false);
  const [hideForToday, setHideForToday] = useState(false);
  const [expiringIngredients, setExpiringIngredients] = useState<IngredientType[]>([]);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [hideExpiryForToday, setHideExpiryForToday] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 960px 미만이 모바일
  const isPureMobile = useMediaQuery('(max-width:480px)'); // 순수 모바일 기기 여부 확인 (480px 이하)
  const { currentUser, setCurrentUser } = useAuth();
  const [headerStoreName, setHeaderStoreName] = useState<string | undefined>(undefined);
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | undefined>(undefined);
  const [layoutStores, setLayoutStores] = useState<{ storeId: number; storeName?: string }[]>([]);
  const [storeMenuAnchor, setStoreMenuAnchor] = useState<null | HTMLElement>(null);
  
  // iOS 키보드 관련 상태 사용 제거

  // 다중 매장: 내 매장 목록 로드 및 전환 시 캐시 무효화 반영
  const loadLayoutStores = React.useCallback(async () => {
    try {
      const res = await storeApi.getMyStores();
      const list = res?.data?.stores ?? [];
      setLayoutStores(Array.isArray(list) ? list : []);
    } catch {
      setLayoutStores([]);
    }
  }, []);
  useEffect(() => {
    if (!currentUser) return;
    loadLayoutStores();
    const onInvalidated = () => loadLayoutStores();
    window.addEventListener('judam:stores-invalidated', onInvalidated);
    return () => window.removeEventListener('judam:stores-invalidated', onInvalidated);
  }, [currentUser, loadLayoutStores]);

  // 상단바 매장/사장명 보강: 항상 최신 매장정보를 조회하여 표시 안정화
  useEffect(() => {
    const fetchStoreName = async () => {
      try {
        const rawId = currentUser?.storeId || (localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : undefined);
        if (!rawId) return;
        const resp = await storeApi.getStoreInfo(rawId);
        const data: any = resp?.data || {};
        const fetchedName = data.name || data.Name || data.storeName || data.StoreName;
        const fetchedOwner = data.ownerName || data.OwnerName || data.owner || data.Owner;
        if (typeof fetchedName === 'string' && fetchedName.trim()) {
          setHeaderStoreName(fetchedName);
          try { localStorage.setItem('storeName', fetchedName); } catch {}
        }
        if (typeof fetchedOwner === 'string' && fetchedOwner.trim()) {
          setOwnerDisplayName(fetchedOwner);
          try { localStorage.setItem('ownerName', fetchedOwner); } catch {}
        }
      } catch (e) {
        // 헤더 표기 보강 용도이므로 오류는 무시
      }
    };
    // 로그인/세션 복원 후 항상 최신값 보강
    fetchStoreName();
    // currentUser에 이미 값이 있으면 즉시 반영하여 초기 깜빡임 감소
    if (currentUser?.storeName) {
      setHeaderStoreName(currentUser.storeName);
    } else if (currentUser?.name && currentUser?.ownerName && currentUser.name !== currentUser.ownerName) {
      // name이 매장명으로 들어온 케이스 대비 (ownerName과 다르면 name을 매장명으로 간주)
      setHeaderStoreName(currentUser.name);
      try { localStorage.setItem('storeName', currentUser.name); } catch {}
    } else {
      const lsStoreName = localStorage.getItem('storeName');
      if (lsStoreName && lsStoreName.trim()) setHeaderStoreName(lsStoreName);
    }
    if (currentUser?.ownerName) {
      setOwnerDisplayName(currentUser.ownerName);
    } else if (currentUser?.name && currentUser?.storeName && currentUser.name !== currentUser.storeName) {
      // name이 사장명으로 들어온 케이스 대비 (storeName과 다르면 name을 사장명으로 간주)
      setOwnerDisplayName(currentUser.name);
      try { localStorage.setItem('ownerName', currentUser.name); } catch {}
    } else {
      const lsOwnerName = localStorage.getItem('ownerName');
      if (lsOwnerName && lsOwnerName.trim()) setOwnerDisplayName(lsOwnerName);
    }
  }, [currentUser]);
  
  // 현재 경로 확인 (다중 매장 메뉴 선택 상태 등, TODO §10)
  const currentPath = location.pathname;
  // 대시보드 탭 클릭 등 "사용자가 명시적으로 대시보드를 원할 때" 멀티매장 첫 화면 리다이렉트를 스킵하기 위한 플래그
  const skipMultiStoreRedirect =
    (location.state as { skipMultiStoreRedirect?: boolean } | null)?.skipMultiStoreRedirect === true ||
    Number(sessionStorage.getItem('skipMultiStoreRedirectAt') ?? 0) > Date.now() - 5000;

  const handleStoreSwitch = (storeId: number, storeName?: string) => {
    setStoreMenuAnchor(null);
    localStorage.setItem('storeId', String(storeId));
    if (storeName) localStorage.setItem('storeName', storeName);
    setCurrentUser((prev) => (prev ? { ...prev, storeId, storeName: storeName ?? prev.storeName } : null));
    window.location.reload();
  };

  // 다중 매장 메뉴: 노출 조건 충족 시에만 표시 (TODO 2.3, 0.8)
  const menuItems = useMemo(() => {
    const items = [...baseMenuItems];
    // 요구사항: 좌측 사이드바에는 다중 매장을 미노출
    // (다중 매장 진입은 프로필 메뉴에서만 제공)
    return items;
  }, []);

  // 다중 매장 조건 미충족 시 리다이렉트 후 토스트 안내 (TODO 2.6)
  useEffect(() => {
    const msg = (location.state as { message?: string } | null)?.message;
    if (msg) {
      showSnackbar(msg, 'info');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, showSnackbar]);

  // 오늘 날짜 문자열 생성 (한국시간 기준)
  const getTodayString = () => {
    // 한국시간(Asia/Seoul, UTC+9) 기준으로 오늘 날짜 계산
    const now = new Date();
    // 한국시간으로 변환 (UTC+9)
    const koreaTimeOffset = 9 * 60; // 한국은 UTC+9 (분 단위)
    const utc = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
    const koreaTime = new Date(utc + (koreaTimeOffset * 60 * 1000));
    
    const year = koreaTime.getFullYear();
    const month = String(koreaTime.getMonth() + 1).padStart(2, '0');
    const day = String(koreaTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD 형식
  };

  // 재고 부족 식재료 체크
  useEffect(() => {
    const checkLowStock = async () => {
      console.log('🔍 재고 부족 체크 시작');
      
      try {
        const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
        const response = await inventoryApi.getIngredients(storeId);
        
        if (response.data && Array.isArray(response.data)) {
          const lowStock = response.data.filter(ingredient => 
            ingredient.currentQuantity <= ingredient.orderThreshold
          );
          
          console.log('📦 재고 부족 식재료:', lowStock.map(item => `${item.name}(ID:${item.id})`));
          
          if (lowStock.length > 0) {
            const today = getTodayString();
            const lowStockIds = lowStock.map(item => item.id).sort();
            
            console.log('📅 오늘 날짜:', today);
            console.log('🆔 현재 재고 부족 ID:', lowStockIds);
            
            // 로컬 스토리지에서 오늘의 숨김 설정 확인
            const hideSettings = localStorage.getItem('lowStockHideSettings');
            let shouldShowDialog = true;
            
            console.log('💾 저장된 숨김 설정:', hideSettings);
            
            if (hideSettings) {
              try {
                const settings = JSON.parse(hideSettings);
                
                // 오늘 날짜이고 "오늘 그만 보기"를 선택했는지 확인
                if (settings.date === today && settings.hideForToday) {
                  // 당일에는 모든 재고 부족 알림 숨김 (상황 변경 여부와 무관)
                    shouldShowDialog = false;
                  console.log('✅ 오늘 그만 보기 설정으로 인해 알림 숨김 (당일 전체 숨김)');
                } else {
                  console.log('📅 다른 날짜이거나 숨김 설정이 없음');
                }
              } catch (error) {
                console.error('재고 부족 숨김 설정 파싱 오류:', error);
              }
            } else {
              console.log('💾 저장된 숨김 설정이 없음');
            }
            
            console.log('🚨 알림 표시 여부:', shouldShowDialog);
            
            if (shouldShowDialog) {
              // 정렬 함수: 숫자 → 한글 → 영어 순서로 정렬
              const sortedLowStock = [...lowStock].sort((a, b) => {
                const getStringType = (str: string) => {
                  const firstChar = str.charAt(0);
                  if (/[0-9]/.test(firstChar)) return 'number';
                  if (/[가-힣]/.test(firstChar)) return 'korean';
                  return 'english';
                };
                
                const typeA = getStringType(a.name);
                const typeB = getStringType(b.name);
                
                // 타입별 우선순위: 숫자 → 한글 → 영어
                const typeOrder = { number: 0, korean: 1, english: 2 };
                
                if (typeOrder[typeA] !== typeOrder[typeB]) {
                  return typeOrder[typeA] - typeOrder[typeB];
                }
                
                // 같은 타입 내에서는 오름차순 정렬
                return a.name.localeCompare(b.name, 'ko', { numeric: true });
              });
              
              setLowStockIngredients(sortedLowStock);
              if (!isMobile) {
                setShowLowStockDialog(true);
                console.log('✅ 재고 부족 알림 다이얼로그 표시 (PC 모드)');
              } else {
                console.log('📱 모바일 모드 - 재고 부족 알림 다이얼로그 표시 건너뜀');
              }
            }
          } else {
            console.log('✅ 재고 부족 식재료 없음');
          }
          
          setLowStockChecked(true);
        }
      } catch (error) {
        console.error('재고 부족 확인 중 오류 발생:', error);
      }
    };

    checkLowStock();
  }, [isMobile]); // PC 모드에서만 다이얼로그 표시

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    // localStorage에 상태 저장
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
  };

  // 현재 드로어 너비 계산
  const currentDrawerWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleLogout = () => {
    // 로그아웃 처리
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleMenuItemClick = (path: string) => {
    if (path === '/dashboard') {
      sessionStorage.setItem('skipMultiStoreRedirectAt', String(Date.now()));
      navigate(path, { state: { skipMultiStoreRedirect: true } });
    } else {
      navigate(path);
    }
    setMobileOpen(false);
  };

  // 재고 부족 다이얼로그 핸들러들
  const handleCloseLowStockDialog = () => {
    console.log('🔘 "나중에 확인" 버튼 클릭됨');
    console.log('📋 현재 hideForToday 상태:', hideForToday);
    
    // 다이얼로그 닫기 전에 강력한 포커스 해제
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    
    // 추가 포커스 해제 (모든 포커스 가능한 요소에서 포커스 제거)
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // body로 포커스 이동하여 완전히 포커스 해제
      document.body.focus();
      document.body.blur();
    }, 0);
    
    // "오늘 그만 보기" 체크 시 로컬 스토리지에 설정 저장
    if (hideForToday) {
      const today = getTodayString();
      const lowStockIds = lowStockIngredients.map(item => item.id).sort();
      
      const hideSettings = {
        date: today,
        hideForToday: true,
        lowStockIds: lowStockIds
      };
      
      console.log('💾 "오늘 그만 보기" 설정 저장:', hideSettings);
      localStorage.setItem('lowStockHideSettings', JSON.stringify(hideSettings));
      console.log('✅ 로컬 스토리지 저장 완료');
    } else {
      console.log('⚠️ "오늘 그만 보기"가 체크되지 않아서 저장하지 않음');
    }
    
    setShowLowStockDialog(false);
    setHideForToday(false); // 체크박스 리셋
    console.log('🔄 다이얼로그 닫기 및 상태 리셋 완료');
  };

  const handleGoToInventory = () => {
    navigate('/inventory?tab=transaction&status=lowStock');
    handleCloseLowStockDialog();
  };

  const handleGoToInventoryTransaction = () => {
    // 재고 관리로 이동할 때도 "오늘 그만 보기" 설정 저장
    if (hideExpiryForToday) {
      const today = getTodayString();
      const expiringIds = expiringIngredients.map(item => item.id.toString()).sort();
      
      const hideSettings = {
        date: today,
        hideForToday: true,
        expiringIds: expiringIds
      };
      
      console.log('💾 재고 관리로 이동할 때 설정 저장:', hideSettings);
      localStorage.setItem('expiryHideSettings', JSON.stringify(hideSettings));
    }
    
    navigate('/inventory?tab=transaction&status=expiring');
    handleCloseExpiryDialog();
  };

  // "오늘 그만 보기" 체크박스 핸들러
  const handleHideForTodayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    console.log('☑️ "오늘 그만 보기" 체크박스 변경:', checked);
    setHideForToday(checked);
  };

  // 숫자 포맷팅 함수
  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 현재수량 포맷팅 함수 (소수점 첫째자리까지, 둘째자리 반올림)
  const formatCurrentQuantity = (num: number): string => {
    const rounded = Math.round(num * 10) / 10; // 소수점 둘째자리 반올림
    return rounded.toFixed(1); // 소수점 첫째자리까지 표시
  };

  const calculateExpiryDDay = (ingredient: IngredientType): DDay => {
    try {
      return computeLayoutExpiryDDay(ingredient);
    } catch (error) {
      console.error('소비기한 계산 중 오류 발생:', error);
      return { text: '-', color: 'default' };
    }
  };

  const handleCloseExpiryDialog = () => {
    // 다이얼로그 닫기 전에 강력한 포커스 해제
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    
    // 추가 포커스 해제 (모든 포커스 가능한 요소에서 포커스 제거)
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // body로 포커스 이동하여 완전히 포커스 해제
      document.body.focus();
      document.body.blur();
    }, 0);
    
    // 다이얼로그를 닫을 때도 "오늘 그만 보기" 설정 저장
    if (hideExpiryForToday) {
      const today = getTodayString();
      const expiringIds = expiringIngredients.map(item => item.id.toString()).sort();
      
      const hideSettings = {
        date: today,
        hideForToday: true,
        expiringIds: expiringIds
      };
      
      console.log('💾 다이얼로그 닫을 때 설정 저장:', hideSettings);
      localStorage.setItem('expiryHideSettings', JSON.stringify(hideSettings));
    }
    
    setShowExpiryDialog(false);
    setHideExpiryForToday(false); // 다이얼로그 닫을 때 체크박스 초기화
  };

  const handleHideExpiryForTodayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setHideExpiryForToday(checked);
  };

  // 소비기한 임박 식재료 체크
  useEffect(() => {
    const checkExpiringIngredients = async () => {
      try {
        console.log('🔍 소비기한 체크 시작');
        const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
        const response = await inventoryApi.getIngredients(storeId);
        const ingredients: IngredientType[] = response.data;
        
        const expiring = ingredients.filter((ingredient: IngredientType) => {
          return isIngredientExpiryAlert(ingredient);
        });

        console.log('📦 소비기한 임박 식재료:', expiring.map(item => `${item.name}(ID:${item.id})`));

        if (expiring.length > 0) {
          const today = getTodayString();
          const expiringIds = expiring.map(item => item.id.toString()).sort();
          const hideSettings = localStorage.getItem('expiryHideSettings');
          let shouldShowDialog = true;

          console.log('📅 오늘 날짜:', today);
          console.log('🆔 현재 소비기한 임박 ID:', expiringIds);
          console.log('💾 저장된 숨김 설정:', hideSettings);

          if (hideSettings) {
            try {
              const settings = JSON.parse(hideSettings);
              console.log('⚙️ 파싱된 설정:', settings);
              
              if (settings.date === today && settings.hideForToday) {
                // 당일에는 모든 소비기한 임박 알림 숨김 (상황 변경 여부와 무관)
                  shouldShowDialog = false;
                console.log('✅ 오늘 그만 보기 설정으로 인해 알림 숨김 (당일 전체 숨김)');
              } else {
                console.log('📅 다른 날짜이거나 숨김 설정이 없음');
              }
            } catch (error) {
              console.error('소비기한 숨김 설정 파싱 오류:', error);
            }
          } else {
            console.log('💾 저장된 숨김 설정이 없음');
          }

          console.log('🚨 알림 표시 여부:', shouldShowDialog);

          if (shouldShowDialog) {
            // D-day 빠른순으로 정렬
            const sortedExpiring = expiring.sort((a, b) => {
              const dayA = computeExpiryDdayFromIngredient(a).daysLeft ?? 999;
              const dayB = computeExpiryDdayFromIngredient(b).daysLeft ?? 999;
              return dayA - dayB;
            });
            
            setExpiringIngredients(sortedExpiring);
            if (!isMobile) {
              setShowExpiryDialog(true);
              console.log('✅ 소비기한 알림 다이얼로그 표시 (PC 모드)');
            } else {
              console.log('📱 모바일 모드 - 소비기한 알림 다이얼로그 표시 건너뜀');
            }
          }
        } else {
          console.log('✅ 소비기한 임박 식재료 없음');
        }
      } catch (error) {
        console.error('소비기한 체크 중 오류 발생:', error);
      }
    };

    // 30분마다 체크
    checkExpiringIngredients();
    const interval = setInterval(checkExpiringIngredients, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isMobile]);  // hideExpiryForToday 의존성 제거, PC 모드에서만 다이얼로그 표시

  useEffect(() => {
    if (isMobile) {
      setShowLowStockDialog(false);
      setShowExpiryDialog(false);
    }
  }, [isMobile]);

  // 식재료 알림 관련 상태 추가
  const [totalAlertCount, setTotalAlertCount] = useState<number>(0);
  
  // 독립적인 배지 데이터 상태 (알림창 표시 여부와 무관)
  const [badgeLowStockIngredients, setBadgeLowStockIngredients] = useState<IngredientType[]>([]);
  const [badgeExpiringIngredients, setBadgeExpiringIngredients] = useState<IngredientType[]>([]);

  // 배지용 재고 부족 및 소비기한 임박 식재료 중복 제거 후 총 개수 계산
  useEffect(() => {
    // 재고 부족 식재료 ID 배열
    const lowStockIds = badgeLowStockIngredients.map(item => item.id);
    // 소비기한 임박 식재료 ID 배열
    const expiryAlertIds = badgeExpiringIngredients.map(item => item.id);
    
    // 두 배열을 합친 후 Set으로 변환하여 유니크한 ID 개수 계산
    const uniqueAlertIds = Array.from(new Set([...lowStockIds, ...expiryAlertIds]));
    const uniqueAlertCount = uniqueAlertIds.length;
    
    setTotalAlertCount(uniqueAlertCount);
    console.log('배지용 총 알림 개수 (중복 제외):', uniqueAlertCount);
    
    // 앱 아이콘 배지 업데이트
    updateAppBadge(uniqueAlertCount).then((success) => {
      if (success) {
        console.log('✅ [앱 배지] 앱 아이콘 배지 업데이트 성공:', uniqueAlertCount);
      } else {
        console.warn('⚠️ [앱 배지] 앱 아이콘 배지 업데이트 실패');
      }
    });
  }, [badgeLowStockIngredients, badgeExpiringIngredients]);

  // 기존 알림창용 상태는 그대로 유지 (하위 호환성)
  useEffect(() => {
    // 재고 부족 식재료 ID 배열
    const lowStockIds = lowStockIngredients.map(item => item.id);
    // 소비기한 임박 식재료 ID 배열
    const expiryAlertIds = expiringIngredients.map(item => item.id);
    
    // 두 배열을 합친 후 Set으로 변환하여 유니크한 ID 개수 계산
    const uniqueAlertIds = Array.from(new Set([...lowStockIds, ...expiryAlertIds]));
    const uniqueAlertCount = uniqueAlertIds.length;
    
    console.log('알림창용 총 알림 개수 (중복 제외):', uniqueAlertCount);
  }, [lowStockIngredients, expiringIngredients]);

  // 배지 데이터 업데이트 함수들
  const updateBadgeData = async () => {
    try {
      console.log('🔄 배지 데이터 업데이트 시작');
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const response = await inventoryApi.getIngredients(storeId);
      
      if (response.data && Array.isArray(response.data)) {
        // 재고 부족 식재료 체크
        const lowStock = response.data.filter(ingredient => 
          ingredient.currentQuantity <= ingredient.orderThreshold
        );
        
        // 소비기한 임박 식재료 체크
        const expiring = response.data.filter(ingredient => isIngredientExpiryAlert(ingredient));
        
        setBadgeLowStockIngredients(lowStock);
        setBadgeExpiringIngredients(expiring);
        
        console.log('✅ 배지 데이터 업데이트 완료:', {
          lowStock: lowStock.length,
          expiring: expiring.length
        });
      }
    } catch (error) {
      console.error('❌ 배지 데이터 업데이트 실패:', error);
    }
  };

  // 초기 배지 데이터 로드
  useEffect(() => {
    updateBadgeData();
    
    // 앱 배지 기능 초기화
    initializeAppBadge().then((success) => {
      if (success) {
        console.log('✅ [앱 배지] 앱 배지 기능 초기화 성공');
      } else {
        console.warn('⚠️ [앱 배지] 앱 배지 기능 초기화 실패 - 지원되지 않는 브라우저일 수 있습니다');
        // 디버깅 정보 출력
        debugAppBadge();
        
      }
    });
    
    // 전역 디버깅 함수 등록
    (window as any).debugAppBadge = debugAppBadge;
    (window as any).updateAppBadge = updateAppBadge;
    
    console.log('🔧 [전역 함수] 디버깅 함수들이 등록되었습니다:');
    console.log('  - debugAppBadge() - 배지 지원 정보 확인');
    console.log('  - updateAppBadge(count) - 배지 업데이트 테스트');

    // Service Worker 메시지 리스너 등록
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type) {
        switch (event.data.type) {
          case 'FALLBACK_APP_BADGE_UPDATE':
            console.log('🔄 [앱 배지] Service Worker 폴백 요청:', event.data.count);
            // 직접 앱 배지 업데이트 시도
            updateAppBadge(event.data.count).then((success) => {
              if (success) {
                console.log('✅ [앱 배지] 폴백 업데이트 성공:', event.data.count);
              } else {
                console.warn('⚠️ [앱 배지] 폴백 업데이트 실패');
              }
            });
            break;
          case 'APP_BADGE_UPDATE_FAILED':
            console.error('❌ [앱 배지] Service Worker 업데이트 실패:', event.data.error);
            // 직접 앱 배지 업데이트 시도
            updateAppBadge(event.data.count).then((success) => {
              if (success) {
                console.log('✅ [앱 배지] 직접 업데이트 성공:', event.data.count);
              } else {
                console.warn('⚠️ [앱 배지] 직접 업데이트도 실패');
              }
            });
            break;
        }
      }
    };

    // Service Worker 메시지 리스너 등록
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // 정리 함수
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  // 매일 새벽 12시 01분에 배지 데이터 새로고침
  useEffect(() => {
    const scheduleDailyRefresh = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 1, 0, 0); // 새벽 12시 01분
      
      const timeUntilMidnight = tomorrow.getTime() - now.getTime();
      
      console.log('⏰ 다음 배지 새로고침 예정:', tomorrow.toLocaleString('ko-KR'));
      
      const timeoutId = setTimeout(() => {
        console.log('🔄 매일 새벽 배지 데이터 새로고침 실행');
        updateBadgeData();
        
        // 다음날을 위해 재귀 호출
        scheduleDailyRefresh();
      }, timeUntilMidnight);
      
      return timeoutId;
    };
    
    const timeoutId = scheduleDailyRefresh();
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const logoSection = (
    <Box 
      sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        minHeight: '80px',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'opacity 160ms cubic-bezier(0.23, 1, 0.32, 1)',
        '&:active': { opacity: 0.85 }
      }}
      onClick={() => {
        sessionStorage.setItem('skipMultiStoreRedirectAt', String(Date.now()));
        navigate('/dashboard', { state: { skipMultiStoreRedirect: true } });
      }}
    >
      <img
        src="/logo120.png"
        alt="주담 클라우드 로고"
        style={{
          height: '40px',
          width: '40px',
          marginRight: collapsed ? '0px' : '12px',
          transition: 'margin-right 200ms cubic-bezier(0.23, 1, 0.32, 1)'
        }}
      />
      {!collapsed && (
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          주방을 담다
        </Typography>
      )}
    </Box>
  );

  const drawer = (
    <>
      {logoSection}
      <Divider />
      <List sx={{ mt: 3 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{ 
              mb: 2,  // 각 메뉴 아이템 사이 마진
              '&:last-child': {
                mb: 0  // 마지막 아이템은 마진 제거
              }
            }}
          >
            <ListItemButton
              onClick={() => handleMenuItemClick(item.path)}
              selected={currentPath === item.path || (item.path === '/multi-store' && currentPath.startsWith('/multi-store'))}
              sx={{
                minHeight: '48px',
                borderRadius: '16px',
                mx: 1,
                justifyContent: collapsed ? 'center' : 'initial',
                transition: 'background-color 160ms cubic-bezier(0.23, 1, 0.32, 1), color 160ms cubic-bezier(0.23, 1, 0.32, 1)',
                '&:active': { transform: 'scale(0.97)' },
                '&.Mui-selected': {
                  background: '#0A302C',
                  backgroundImage: 'none',
                  color: 'primary.contrastText',
                  '&:hover': {
                    background: '#185851',
                    backgroundImage: 'none'
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText'
                  }
                }
              }}
            >
              <ListItemIcon
                sx={{
                  color: (currentPath === item.path || (item.path === '/multi-store' && currentPath.startsWith('/multi-store'))) ? 'white' : 'inherit',
                  minWidth: collapsed ? 'auto' : 40,
                  justifyContent: 'center',
                  transition: 'color 160ms cubic-bezier(0.23, 1, 0.32, 1)'
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: 'navMenu',
                    sx: {
                      color: (currentPath === item.path || (item.path === '/multi-store' && currentPath.startsWith('/multi-store'))) ? 'white' : 'inherit'
                    }
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  const showStoreSwitcher = FEATURE_MULTI_STORE && layoutStores.length >= 2;
  const headerStoreLabel =
    headerStoreName
    || currentUser?.storeName
    || ((currentUser?.name && currentUser?.ownerName && currentUser.name !== currentUser.ownerName) ? currentUser.name : '');
  const headerOwnerLabel =
    ownerDisplayName
    || currentUser?.ownerName
    || ((currentUser?.name && currentUser?.storeName && currentUser.name !== currentUser.storeName) ? currentUser.name : '');

  return (
    <BadgeProvider updateBadgeData={updateBadgeData}>
      <Box 
        sx={{ 
          minHeight: '100vh',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}
    >
      <CssBaseline />
      
      {/* 모바일 헤더 */}
      {isMobile && <MobileHeader />}
      
      {/* 데스크톱 헤더 */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 200ms cubic-bezier(0.23, 1, 0.32, 1), margin-left 200ms cubic-bezier(0.23, 1, 0.32, 1)',
          // PC모드에서 모든 모서리 라운드 제거
          borderRadius: { md: '0px' },
          borderTopLeftRadius: { md: '0px' },
          borderTopRightRadius: { md: '0px' },
          borderBottomLeftRadius: { md: '0px' },
          borderBottomRightRadius: { md: '0px' },
          zIndex: 1200, // PC 모드 AppBar z-index 명시적 설정 (테이블 헤더보다 높게)
        }}
      >
        <Toolbar
          sx={{
            minHeight: '80px !important',
            '@media (min-width: 600px)': {
              minHeight: '80px !important'
            }
          }}
          >
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerCollapse}
            edge="start"
            sx={{ mr: 2 }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {showStoreSwitcher && (
            <>
              <Button
                color="inherit"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setStoreMenuAnchor(e.currentTarget)}
                sx={{ mr: 1, textTransform: 'none' }}
              >
                {headerStoreLabel || '매장'}
              </Button>
              <Menu
                anchorEl={storeMenuAnchor}
                open={Boolean(storeMenuAnchor)}
                onClose={() => setStoreMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {layoutStores
                  .filter((s) => s.storeId !== (currentUser?.storeId ?? parseInt(localStorage.getItem('storeId') || '0', 10)))
                  .map((s) => (
                    <MenuItem key={s.storeId} onClick={() => handleStoreSwitch(s.storeId, s.storeName)}>
                      {s.storeName || `매장 #${s.storeId}`}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
            <ButtonBase
              aria-label="계정 메뉴 열기"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={accountButtonSx}
            >
              <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
                {/* 매장 전환 버튼이 이미 매장명을 보여주므로 그때는 사장님만 표기 */}
                {!showStoreSwitcher && (
                  <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 650, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {headerStoreLabel}
                  </Typography>
                )}
                {headerOwnerLabel && (
                  // 색 대신 opacity — AppBar가 하위 Typography 색을 inherit로 강제한다.
                  <Typography
                    noWrap
                    sx={{
                      fontSize: showStoreSwitcher ? '0.9375rem' : '0.75rem',
                      fontWeight: showStoreSwitcher ? 650 : 500,
                      lineHeight: 1.3,
                      opacity: showStoreSwitcher ? 1 : 0.72,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {headerOwnerLabel} 사장님
                  </Typography>
                )}
              </Box>
              <Avatar sx={{ width: 34, height: 34, backgroundColor: 'rgba(255, 255, 255, 0.12)' }}>
                <ReportHdrIcon file="icons8-customer-100" w={26} />
              </Avatar>
              <ArrowDropDownIcon sx={{ ml: -0.5, opacity: 0.72 }} />
            </ButtonBase>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
                {FEATURE_MULTI_STORE && layoutStores.length >= 2 && (
                  <MenuItem
                    onClick={() => {
                      navigate('/multi-store');
                      handleClose();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.5,
                      px: 2
                    }}
                  >
                    <ReportHdrIcon file="icons8-online-store-100" w={18} />
                    다점포 관리
                  </MenuItem>
                )}
                <MenuItem
                  onClick={handleProfile}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2
                  }}
                >
                  <ReportHdrIcon file="icons8-test-account-100" w={18} />
                  프로필
                </MenuItem>
                <MenuItem
                  onClick={handleSettings}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2
                  }}
                >
                  <ReportHdrIcon file="icons8-settings-100" w={18} />
                  설정
                </MenuItem>
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2
                  }}
                >
                  <ReportHdrIcon file="icons8-logout-100" w={18} />
                  로그아웃
                </MenuItem>
            </Menu>
        </Toolbar>
      </AppBar>
      
      <Box sx={{ 
        flex: 1,
        minHeight: 0,
        display: 'flex',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* 사이드바 (데스크톱/태블릿용) */}
        <Box
          component="nav"
          sx={{ 
            width: { md: currentDrawerWidth }, 
            flexShrink: { md: 0 },
            display: { xs: 'none', md: 'block' },
            transition: 'width 200ms cubic-bezier(0.23, 1, 0.32, 1)'
          }}
          aria-label="navigation menu"
        >
          {/* 모바일용 임시 드로어 (햄버거 메뉴용) */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // 성능 향상
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                top: mobileHeaderOffset,
                height: mobileDrawerHeight,
                overflowY: 'auto'
              }
            }}
          >
            {drawer}
          </Drawer>
          {/* 데스크톱용 영구 드로어 */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: currentDrawerWidth,
                position: 'relative',
                whiteSpace: 'nowrap',
                borderRight: '1px solid rgba(0, 0, 0, 0.12)', // 우측 경계선 추가
                height: '100vh', // 화면 끝까지 높이 설정
                transition: 'width 200ms cubic-bezier(0.23, 1, 0.32, 1)',
                overflowX: 'hidden', // 가로 스크롤 숨기기
                // PC모드에서 상단 모서리 라운드 제거 (하단만 유지)
                borderTopLeftRadius: { md: '0px' },
                borderTopRightRadius: { md: '0px' }
              }
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        
        {/* 메인 콘텐츠 - minHeight: 0으로 flex 내부 스크롤 활성화 (메뉴분석/재고분석 등) */}
        <Box
          component="main"
          sx={{ 
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'auto',
            backgroundColor: 'var(--app-bg)',
            p: 0,
            width: isMobile ? '100%' : { md: `calc(100% - ${currentDrawerWidth}px)` },
            maxWidth: '100%',
            paddingTop: isMobile ? mobileHeaderOffset : desktopMainPaddingTop,
            paddingBottom: isMobile ? mobileBottomOffset : desktopMainPaddingBottom,
              WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* 다중 매장 첫 화면 리다이렉트: 매장 2개 이상 + 플래그 ON일 때 / 또는 /dashboard 진입 시 다중 매장 대시보드로. 로딩 전(layoutStores 빈 배열)에는 리다이렉트하지 않음. */}
          {FEATURE_MULTI_STORE &&
          !skipMultiStoreRedirect &&
          layoutStores.length >= 2 &&
          (location.pathname === '/' || location.pathname === '/dashboard') ? (
            <Navigate to="/multi-store/dashboard" replace />
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>

      {/* 모바일 하단 네비게이션 */}
      {isMobile && (
          <MobileBottomNavigation 
            lowStockCount={lowStockIngredients.length} 
            expiryAlertCount={expiringIngredients.length}
            totalAlertCount={totalAlertCount}
          />
      )}

      {/* 재고 부족 경고 다이얼로그 */}
      <Dialog 
        open={showLowStockDialog} 
        onClose={handleCloseLowStockDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: isMobile ? '95%' : undefined,
            margin: isMobile ? '16px' : undefined,
            maxHeight: isMobile ? '67.5vh' : '80vh', // 모바일: 67.5vh (90vh의 75%), PC: 80vh
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: isMobile ? 2 : 3,
          fontSize: isMobile ? '1rem' : '1.25rem',
          flexShrink: 0 // 제목 영역 고정
        }}>
          재고 부족 알림
        </DialogTitle>
        <DialogContent sx={{ 
          p: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // DialogContent 자체는 스크롤 없음
          flex: '1 1 auto',
          minHeight: 0 // flexbox에서 스크롤 가능하도록
        }}>
          <Alert severity="warning" sx={{ mb: 2, mt: 2, flexShrink: 0 }}>
            <Box sx={{ 
              display: isMobile || isPureMobile ? 'block' : 'flex',
              flexDirection: isMobile || isPureMobile ? 'column' : 'row',
              gap: isMobile || isPureMobile ? 0.5 : 1,
              alignItems: isMobile || isPureMobile ? 'flex-start' : 'center'
            }}>
              <Typography variant="body1" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                <strong>{lowStockIngredients.length}개의 식재료</strong>가 발주 기준 이하로 떨어졌습니다.
              </Typography>
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                신속한 발주 처리가 필요합니다.
              </Typography>
            </Box>
          </Alert>
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 2,
              maxHeight: isMobile ? '33.75vh' : '50vh', // 모바일: 33.75vh (45vh의 75%), PC: 50vh
              overflow: 'auto',
              flex: '1 1 auto',
              minHeight: 0, // flexbox에서 스크롤 가능하도록
              position: 'relative',
              borderRadius: 0 // Paper의 기본 border-radius 제거하여 마지막 행 border가 잘리지 않도록
            }}
            ref={lowStockTableRef as any}
          >
            <Table size={isMobile ? "small" : "medium"} stickyHeader={false}>
              <TableHead sx={{
                position: 'sticky',
                top: 0,
                zIndex: 9999,
                backgroundColor: 'white'
              }}>
                <TableRow>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                      p: isMobile ? 1 : 2,
                      fontWeight: 'bold',
                      width: isPureMobile ? '50%' : 'auto',
                      maxWidth: isPureMobile ? '50%' : 'auto',
                      wordBreak: isPureMobile ? 'break-word' : 'normal'
                    }}
                  >
                    식재료명
                  </TableCell>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                      p: isMobile ? 1 : 2,
                      fontWeight: 'bold',
                      width: isPureMobile ? '50%' : 'auto',
                      maxWidth: isPureMobile ? '50%' : 'auto',
                      wordBreak: isPureMobile ? 'break-word' : 'normal',
                      '&:last-child': {
                        borderTopRightRadius: '16px'
                      }
                    }}
                  >
                    현재 수량
                  </TableCell>
                  {!isPureMobile && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                        whiteSpace: 'nowrap',
                      p: isMobile ? 1 : 2,
                        fontWeight: 'bold'
                    }}
                  >
                    발주 기준
                  </TableCell>
                  )}
                  {!isPureMobile && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                        whiteSpace: 'nowrap',
                      p: isMobile ? 1 : 2,
                      fontWeight: 'bold',
                      '&:last-child': {
                        borderTopRightRadius: '16px'
                      }
                    }}
                  >
                    거래처
                  </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {lowStockIngredients.map((ingredient, index) => {
                  const isLastRow = index === lowStockIngredients.length - 1;
                  return (
                  <TableRow 
                    key={ingredient.id}
                    sx={{
                      '&:last-child td': {
                        borderBottom: isLastRow ? '1px solid rgba(224, 224, 224, 1)' : 'none'
                      }
                    }}
                  >
                    <TableCell align="center" sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      p: isMobile ? 1 : 2,
                      width: isPureMobile ? '50%' : 'auto',
                      maxWidth: isPureMobile ? '50%' : 'auto',
                      whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                      wordBreak: isPureMobile ? 'break-word' : 'normal',
                      overflow: 'hidden'
                    }}>
                      {ingredient.name}
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      p: isMobile ? 1 : 2,
                      color: 'error.main',
                      width: isPureMobile ? '50%' : 'auto',
                      maxWidth: isPureMobile ? '50%' : 'auto',
                      whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                      wordBreak: isPureMobile ? 'break-word' : 'normal',
                      overflow: 'hidden'
                    }}>
                      {ingredient.currentQuantity}{ingredient.currentQuantityUnit || '개'}
                    </TableCell>
                    {!isPureMobile && (
                    <TableCell align="center" sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                        p: isMobile ? 1 : 2
                    }}>
                      {ingredient.orderThreshold}{ingredient.currentQuantityUnit || '개'}
                    </TableCell>
                    )}
                    {!isPureMobile && (
                    <TableCell align="center" sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                        p: isMobile ? 1 : 2
                    }}>
                      {ingredient.supplierName || '-'}
                    </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* 항목 수 표시 */}
          <Box sx={{ 
            mt: 1, 
            display: 'flex', 
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Typography variant="caption" sx={{ 
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              color: 'text.secondary'
            }}>
              총 {lowStockIngredients.length}개 항목
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: 'column', 
          gap: 1, 
          p: isMobile ? 2 : 3,
          bgcolor: 'background.default',
          flexShrink: 0 // 버튼 영역 고정
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideForToday}
                onChange={handleHideForTodayChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            }
            label={
              <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                오늘 그만 보기 (새로운 재고 부족 발생 시 다시 알림)
              </Typography>
            }
            sx={{ alignSelf: 'flex-start' }}
          />
          
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              onClick={handleCloseLowStockDialog}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            >
              닫기
            </Button>
            <Button 
              onClick={handleGoToInventory}
              fullWidth
              variant="contained"
              size={isMobile ? "small" : "medium"}
            >
              재고 관리로 이동
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* 소비기한 경고 다이얼로그 */}
      <Dialog 
        open={showExpiryDialog} 
        onClose={handleCloseExpiryDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: isMobile ? '95%' : undefined,
            margin: isMobile ? '16px' : undefined,
            maxHeight: isMobile ? '67.5vh' : '80vh', // 모바일: 67.5vh (90vh의 75%), PC: 80vh
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: isMobile ? 2 : 3,
          fontSize: isMobile ? '1rem' : '1.25rem',
          flexShrink: 0 // 제목 영역 고정
        }}>
          소비기한 임박 알림
        </DialogTitle>
        <DialogContent sx={{ 
          p: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // DialogContent 자체는 스크롤 없음
          flex: '1 1 auto',
          minHeight: 0 // flexbox에서 스크롤 가능하도록
        }}>
          <Alert severity="warning" sx={{ mb: 2, mt: 2, flexShrink: 0 }}>
            <Box sx={{ 
              display: isMobile || isPureMobile ? 'block' : 'flex',
              flexDirection: isMobile || isPureMobile ? 'column' : 'row',
              gap: isMobile || isPureMobile ? 0.5 : 1,
              alignItems: isMobile || isPureMobile ? 'flex-start' : 'center'
            }}>
              <Typography variant="body1" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                <strong>{expiringIngredients.length}개의 식재료</strong>가 소비기한에 임박했습니다.
              </Typography>
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                신속한 확인이 필요합니다.
              </Typography>
            </Box>
          </Alert>
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 2,
              maxHeight: isMobile ? '33.75vh' : '50vh', // 모바일: 33.75vh (45vh의 75%), PC: 50vh
              overflow: 'auto',
              flex: '1 1 auto',
              minHeight: 0, // flexbox에서 스크롤 가능하도록
              position: 'relative',
              borderRadius: 0 // Paper의 기본 border-radius 제거하여 마지막 행 border가 잘리지 않도록
            }}
            ref={expiryTableRef as any}
          >
            <Table size={isMobile ? "small" : "medium"} stickyHeader={false}>
              <TableHead sx={{
                position: 'sticky',
                top: 0,
                zIndex: 9999,
                backgroundColor: 'white'
              }}>
                <TableRow>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                      p: isMobile ? 1 : 2,
                      fontWeight: 'bold',
                      width: isPureMobile ? '50%' : 'auto',
                      maxWidth: isPureMobile ? '50%' : 'auto',
                      wordBreak: isPureMobile ? 'break-word' : 'normal'
                    }}
                  >
                    식재료명
                  </TableCell>
                  {!isPureMobile && (
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        whiteSpace: 'nowrap',
                        p: isMobile ? 1 : 2,
                        fontWeight: 'bold'
                      }}
                    >
                      브랜드명
                    </TableCell>
                  )}
                  {!isPureMobile && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                        whiteSpace: 'nowrap',
                      p: isMobile ? 1 : 2,
                        fontWeight: 'bold'
                    }}
                  >
                    소비기한
                  </TableCell>
                  )}
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                      p: isMobile ? 1 : 2,
                      fontWeight: 'bold',
                      width: isPureMobile ? '50%' : 'auto',
                      maxWidth: isPureMobile ? '50%' : 'auto',
                      wordBreak: isPureMobile ? 'break-word' : 'normal',
                      '&:last-child': {
                        borderTopRightRadius: '16px'
                      }
                    }}
                  >
                    D-Day
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiringIngredients.map((ingredient, index) => {
                  const dDay = calculateExpiryDDay(ingredient);
                  const isLastRow = index === expiringIngredients.length - 1;
                  return (
                    <TableRow 
                      key={ingredient.id}
                      sx={{
                        '&:last-child td': {
                          borderBottom: isLastRow ? '1px solid rgba(224, 224, 224, 1)' : 'none'
                        }
                      }}
                    >
                      <TableCell align="center" sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        p: isMobile ? 1 : 2,
                        width: isPureMobile ? '50%' : 'auto',
                        maxWidth: isPureMobile ? '50%' : 'auto',
                        whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                        wordBreak: isPureMobile ? 'break-word' : 'normal',
                        overflow: 'hidden'
                      }}>
                        {ingredient.name}
                      </TableCell>
                      {!isPureMobile && (
                        <TableCell align="center" sx={{ 
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          p: isMobile ? 1 : 2
                        }}>
                          {ingredient.brand || ingredient.brandName || '-'}
                        </TableCell>
                      )}
                      {!isPureMobile && (
                      <TableCell align="center" sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                          p: isMobile ? 1 : 2
                      }}>
                        {ingredient.expiryDate ? new Date(ingredient.expiryDate).toLocaleDateString() : '-'}
                      </TableCell>
                      )}
                      <TableCell align="center" sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        p: isMobile ? 1 : 2,
                        width: isPureMobile ? '50%' : 'auto',
                        maxWidth: isPureMobile ? '50%' : 'auto',
                        whiteSpace: isPureMobile ? 'normal' : 'nowrap',
                        wordBreak: isPureMobile ? 'break-word' : 'normal',
                        overflow: 'hidden'
                      }}>
                        <ExpiryChip label={dDay.text} tone={layoutExpiryColorToTone(dDay.color)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* 항목 수 표시 */}
          <Box sx={{ 
            mt: 1, 
            display: 'flex', 
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Typography variant="caption" sx={{ 
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              color: 'text.secondary'
            }}>
              총 {expiringIngredients.length}개 항목
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: 'column', 
          gap: 1, 
          p: isMobile ? 2 : 3,
          bgcolor: 'background.default',
          flexShrink: 0 // 버튼 영역 고정
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideExpiryForToday}
                onChange={handleHideExpiryForTodayChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            }
            label={
              <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                오늘 그만 보기 (새로운 소비기한 임박 발생 시 다시 알림)
              </Typography>
            }
            sx={{ alignSelf: 'flex-start' }}
          />
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              onClick={handleCloseExpiryDialog}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            >
              닫기
            </Button>
            <Button
              onClick={handleGoToInventoryTransaction}
              fullWidth
              variant="contained"
              size={isMobile ? "small" : "medium"}
            >
              재고 관리로 이동
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      </Box>
    </BadgeProvider>
  );
};

export default Layout; 

